import React, { useState } from "react";
import InputField from "../generic/InputField";
import FormComponent from "../generic/FormComponent";
import AddAnotherItemButton from "../generic/AddAnotherItemButton";
import SubmitButton from "../generic/SubmitButton";
import RemoveButton from "../generic/RemoveButton";
import ButtonGroupOrganizer from "../generic/ButtonGroupOrganizer";
import { Messages } from "../../constants/Messages";
import SharedAlertBanner from "../shared/SharedAlertBanner";
import { handleMaterialResponseAlerts, noDataValidation, normalizeString } from "../../utils/AlertUtils";
import RowItem from "../generic/RowItem"; // Import RowItem

interface CreateMaterialFormProps {
    title: string;
    fieldName: string;
    onSubmit: (items: string[]) => Promise<any>;
    formatSuccessMessage: (results: any[]) => string;
    onCloseModal: () => void;
}

const CreateMaterialForm: React.FC<CreateMaterialFormProps> = ({
                                                                   title,
                                                                   fieldName,
                                                                   onSubmit,
                                                                   formatSuccessMessage,
                                                                   onCloseModal,
                                                               }) => {
    const [items, setItems] = useState<{ value: string }[]>([{ value: "" }]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [successAlert, setSuccessAlert] = useState<{ message: string } | null>(null);
    const [errorAlert, setErrorAlert] = useState<{ message: string } | null>(null);

    const handleItemChange = (index: number, value: string) => {
        const updatedItems = [...items];
        updatedItems[index].value = value;
        setItems(updatedItems);
        clearAlerts();
    };

    const clearAlerts = () => {
        setSuccessAlert(null);
        setErrorAlert(null);
    };

    const addNewItem = () => {
        setItems((prevItems) => [...prevItems, { value: "" }]);
        clearAlerts();
    };

    const removeItem = (index: number) => {
        setItems((prevItems) => prevItems.filter((_, i) => i !== index));
        clearAlerts();
    };

    const handleSubmit = async () => {
        const validItems = items.filter((_data: any) => _data.value.trim() !== "");
        noDataValidation(validItems, setErrorAlert);

        setIsLoading(true);

        try {
            const response = await onSubmit(items.map((item) => item.value));
            handleMaterialResponseAlerts(response, setSuccessAlert, setErrorAlert, formatSuccessMessage);

            if (response.errors && response.errors.length > 0) {
                const invalidValues = response.errors.map((error: any) => error.value);

                const invalidItems = items.filter((item) =>
                    invalidValues.some((invalidValue: string) =>
                        Object.keys(invalidValue).every(
                            (key) => invalidValue.trim().toLowerCase() === item.value.trim().toLowerCase(),
                        ),
                    ),
                );

                setItems(invalidItems);
            } else {
                setErrorAlert(null);
                setTimeout(() => {
                    onCloseModal();
                }, 1000);
            }
        } catch (error) {
            console.error("Error submitting material:", error);
            setErrorAlert({
                message: Messages.ERROR_CREATE,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const checkForDuplicateInRow = (index: number): boolean => {
        const currentValue = normalizeString(items[index].value);
        return items.some((item, i) => i !== index && normalizeString(item.value) === currentValue);
    };

    return (
        <FormComponent title={title} isModal={true}>
            <SharedAlertBanner
                successAlert={successAlert}
                errorAlert={errorAlert}
                onCloseSuccess={() => setSuccessAlert(null)}
                onCloseError={() => setErrorAlert(null)}
            />

            {items.map((item, index) => (
                <RowItem
                    key={index}
                    index={index}
                    showRemoveButton={items.length > 1}
                    onRemove={removeItem}
                >
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold mb-2">
                            {fieldName} {index + 1}
                        </h3>
                    </div>
                    <InputField
                        type="text"
                        value={item.value}
                        onChange={(value) => handleItemChange(index, value)}
                        placeholder={`Enter ${fieldName.toLowerCase()}`}
                        required
                    />
                    {checkForDuplicateInRow(index) && (
                        <p className="text-red-600 text-sm mt-2">This {fieldName.toLowerCase()} already exists.</p>
                    )}
                </RowItem>
            ))}

            <ButtonGroupOrganizer>
                <AddAnotherItemButton onAdd={addNewItem} />
                <SubmitButton
                    isLoading={isLoading}
                    disabled={
                        items.some((item) => item.value.trim() === "") ||
                        items.some((_, i) => checkForDuplicateInRow(i)) ||
                        isLoading
                    }
                    onClick={handleSubmit}
                />
            </ButtonGroupOrganizer>
        </FormComponent>
    );
};

export default CreateMaterialForm;
